<template>
  <div class="max-w-sm mx-auto">
    <div class="overflow-hidden border border-gray-200 dark:border-gray-800 sm:rounded-lg bg-gray-50 dark:bg-gray-800/50">
      <p v-if="!breaks.length" class="p-2 text-sm">
        {{ $t('roster.noBreaks') }}
      </p>
      <table v-else class="min-w-full divide-y bg-white dark:bg-gray-950 divide-gray-100">
        <thead class="bg-gray-50 border-b border-gray-200 dark:border-gray-800 dark:bg-gray-800/50 dark:divide-gray-800">
          <tr>
            <th scope="col" class="px-2 py-2 text-left text-xs font-medium opacity-60 uppercase tracking-wider">
              {{ $t('general.start') }}
            </th>
            <th scope="col" class="px-2 py-2 text-left text-xs font-medium opacity-60 uppercase tracking-wider">
              {{ $t('general.duration') }}
            </th>
            <th scope="col" class="px-2 py-2 text-left text-xs font-medium opacity-60 uppercase tracking-wider">
              {{ $t('general.label') }}
            </th>
            <th scope="col" class="px-2 py-2 text-left text-xs font-medium opacity-60 uppercase tracking-wider flex justify-end">
              {{ $t('general.remove') }}
            </th>
          </tr>
        </thead>
        <tbody class="bg-gray-50/50 divide-y divide-gray-100 dark:bg-gray-800/50 dark:border-gray-800 dark:divide-gray-800 text-sm">
          <tr v-for="(item, index) in breaks" :key="index + '-break'">
            <td class="px-2 py-2 whitespace-nowrap">
              {{ $date.minutes(item.time) }}
            </td>
            <td class="px-2 py-2 whitespace-nowrap">
              {{ item.duration }} min
            </td>
            <td class="px-2 py-2 whitespace-nowrap">
              {{ item.label || '-' }}
            </td>
            <td class="px-2 py-2 whitespace-nowrap flex justify-end">
              <o-button variant="danger" flat size="sm" icon="delete" @click="handleBreakRemove(index)" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="mt-1 border border-gray-200 dark:border-gray-800 sm:rounded-lg bg-gray-50 dark:bg-gray-800/50 p-2">
      <p class="text-sm opacity-60 font-medium">
        {{ $t('roster.addBreak') }}
      </p>
      <o-alert :show="!isDuringWorkingHours" variant="warning" base-class="rounded p-2 relative flex items-start relative" class="my-2">
        <p class="text-sm pt-px">
          {{ $t('components.roster.elements.edit.rosterEditBreaks.breakOutsideHours') }}
        </p>
      </o-alert>
      <o-form ref="form">
        <div class="flex items-start space-x-2">
          <o-field-group v-slot="{ status }" :label="$t('general.time')" :name="$t('general.time')" label-class="text-xs opacity-60" rules="required">
            <time-field v-model="newBreak.time" :augmenter="Augmenters.minutes" :status="status" />
          </o-field-group>

          <o-field-group v-slot="{ status }" :label="$t('general.duration')" :name="$t('general.duration')" label-class="text-xs opacity-60" rules="required|between:0,500">
            <number-field v-model="newBreak.duration" :min="0" :max="500" slim :status="status" />
          </o-field-group>

          <o-field-group :label="$t('general.label')" label-class="text-xs opacity-60 pt-3px">
            <text-field v-model="newBreak.label" />
          </o-field-group>

          <o-button variant="info" flat class="h-8 mb-px mt-6" @click="handleBreakAdd()">
            Add
          </o-button>
        </div>
      </o-form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

import {
  TimeField,
  NumberField,
  TextField,
} from '@/components/fields'

import {
  OAlert,
} from '@/components/elements'

import { EmployeeBreak } from '@/_api/models/employeeBreak'

interface Props {
  breaks: any[]
  day: any
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:breaks', value: any[]): void
}>()

const form = ref<any>()

const newBreak = ref({
  time: 720,
  duration: 30,
  label: '',
})

const _breaks = computed({
  get() {
    return props.breaks
  },
  set(value: any[]) {
    emit('update:breaks', value)
  },
})

const isDuringWorkingHours = computed(() => {
  const { day } = props

  const dayStartTime = day.hour.startTime
  const dayFinishTime = day.hour.finishTime

  const breakStartTime = newBreak.value.time
  const breakDuration = newBreak.value.duration
  const breakFinishTime = breakStartTime + breakDuration

  const isBeforeStart = breakStartTime >= dayStartTime
  const isBeforeFinish = (breakStartTime < dayFinishTime) && (breakFinishTime <= dayFinishTime)

  return isBeforeStart && isBeforeFinish
})

function handleBreakAdd() {
  const valid = validate()

  if (valid) {
    const data = newBreak.value
    const b = new EmployeeBreak(data)

    _breaks.value.push(b)

    newBreak.value = {
      time: 720,
      duration: 30,
      label: '',
    }
  }
}

function handleBreakRemove(index: number) {
  _breaks.value.splice(index, 1)
}

function validate() {
  return form.value.validate()
}
</script>
